import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/client';
import { Organization } from '../../types';

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  PENDING: 'warning',
  ACTIVE: 'success',
  TRIAL: 'info',
  EXPIRED: 'error',
  SUSPENDED: 'default',
};

const statusLabels: Record<string, string> = {
  PENDING: 'Afventer',
  ACTIVE: 'Aktiv',
  TRIAL: 'Prøveperiode',
  EXPIRED: 'Udløbet',
  SUSPENDED: 'Suspenderet',
};

export const AdminDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const res = await adminApi.getStats();
      return res.data.data;
    },
  });

  const { data: pendingOrgs, isLoading: pendingLoading } = useQuery({
    queryKey: ['admin', 'organizations', 'pending'],
    queryFn: async () => {
      const res = await adminApi.getOrganizations({ status: 'PENDING' });
      return res.data.data;
    },
  });

  const { data: recentOrgs, isLoading: recentLoading } = useQuery({
    queryKey: ['admin', 'organizations', 'recent'],
    queryFn: async () => {
      const res = await adminApi.getOrganizations({ limit: 10 });
      return res.data.data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: (orgId: string) => adminApi.approveOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (orgId: string) => adminApi.rejectOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });

  if (statsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Super Admin Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                  }}
                >
                  <BusinessIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {stats?.organizations?.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organisationer
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.light',
                    color: 'success.main',
                  }}
                >
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {stats?.organizations?.active || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktive
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'warning.light',
                    color: 'warning.main',
                  }}
                >
                  <PendingIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {stats?.organizations?.pending || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Afventer
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'info.light',
                    color: 'info.main',
                  }}
                >
                  <PeopleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {stats?.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Brugere
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Applications */}
      {pendingOrgs && pendingOrgs.length > 0 && (
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Afventende ansøgninger
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>CVR</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Ansøgt</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrgs.map((org: Organization) => (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{org.name}</Typography>
                      </TableCell>
                      <TableCell>{org.cvr || '-'}</TableCell>
                      <TableCell>{org.email}</TableCell>
                      <TableCell>
                        {new Date(org.createdAt).toLocaleDateString('da-DK')}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => approveMutation.mutate(org.id)}
                          disabled={approveMutation.isPending}
                          sx={{ mr: 1, borderRadius: 1 }}
                        >
                          Godkend
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => rejectMutation.mutate(org.id)}
                          disabled={rejectMutation.isPending}
                          sx={{ borderRadius: 1 }}
                        >
                          Afvis
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Organizations */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Seneste organisationer
          </Typography>
          {recentLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>CVR</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Prøveperiode</TableCell>
                    <TableCell>Oprettet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrgs?.map((org: Organization) => (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{org.name}</Typography>
                      </TableCell>
                      <TableCell>{org.cvr || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabels[org.status] || org.status}
                          color={statusColors[org.status] || 'default'}
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        {org.trialEndDate
                          ? new Date(org.trialEndDate).toLocaleDateString('da-DK')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {new Date(org.createdAt).toLocaleDateString('da-DK')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboard;
